import type { ReportStandardFundForPrivateAnnualDataModel } from '@/features/mold/models/standard-fund';
import {
  convertToNegative,
  formatAmountReport,
  validateTotalWithElements,
} from '@/utils';
import type { z } from 'zod';

type StandardFundReportForPrivateValueConverterArgs = {
  lastYearBeginningOfTheFundBalance: number | undefined | null;
  lastYearRawBeginningOfTheFundBalance: number | undefined | null;
  annualData:
    | z.infer<typeof ReportStandardFundForPrivateAnnualDataModel>
    | undefined;
  type: 'csv' | 'pdf';
  isDebugMode: boolean;
};
export const standardFundReportForPrivateValueConverter = ({
  lastYearBeginningOfTheFundBalance,
  lastYearRawBeginningOfTheFundBalance,
  annualData,
  type,
  isDebugMode,
}: StandardFundReportForPrivateValueConverterArgs) => {
  const { result: isNullEndingOfTheFundBalance } = validateTotalWithElements({
    totalValue: annualData?.endingOfTheFundBalance,
    elements: [
      {
        value: lastYearBeginningOfTheFundBalance,
        rawValue: lastYearRawBeginningOfTheFundBalance,
        negativeCount: 0,
      },
      {
        value: annualData?.totalIncome,
        rawValue: annualData?.rawTotalIncome,
        negativeCount: annualData?.negativeTotalIncomeCount,
      },
      {
        value: convertToNegative(annualData?.totalExpenses),
        rawValue: convertToNegative(annualData?.rawTotalExpenses),
        negativeCount: annualData?.negativeTotalExpensesCount,
      },
      {
        value: convertToNegative(
          annualData?.amountReturnedToTheNationalTreasury,
        ),
        rawValue: convertToNegative(
          annualData?.rawAmountReturnedToTheNationalTreasury,
        ),
        negativeCount: 0,
      },
      {
        value: convertToNegative(annualData?.amountReturnedToTheOrganization),
        rawValue: convertToNegative(
          annualData?.rawAmountReturnedToTheOrganization,
        ),
        negativeCount: 0,
      },
    ],
  });
  return {
    totalIncome: formatAmountReport(annualData?.totalIncome, type, {
      isDebugMode,
      negativeCount: annualData?.negativeTotalIncomeCount,
      rawValue: type === 'pdf' ? annualData?.rawTotalIncome : undefined,
    }),
    totalExpenses: formatAmountReport(annualData?.totalExpenses, type, {
      isDebugMode,
      negativeCount: annualData?.negativeTotalExpensesCount,
      rawValue: type === 'pdf' ? annualData?.rawTotalExpenses : undefined,
    }),
    endingOfTheFundBalance: formatAmountReport(
      annualData?.endingOfTheFundBalance,
      type,
      {
        isDebugMode,
        negativeCount:
          isNullEndingOfTheFundBalance === 'isNull' ||
          annualData?.isCanceledEndingOfTheFundBalance
            ? 1
            : 0,
        rawValue:
          type === 'pdf' ? annualData?.rawEndingOfTheFundBalance : undefined,
      },
    ),
  };
};
